import { ActionLink } from "@diffex/ui";

import { PublicLayout } from "../components/PublicLayout";
import { RequestId } from "../components/RequestId";
import { productConfig } from "../config";

const authenticationMessages = {
  access_denied: {
    title: "Sign in was canceled",
    detail: "No changes were made. You can try again whenever you are ready.",
  },
  invite_required: {
    title: "An invitation is required",
    detail: "Diffex is currently available to invited members only.",
  },
  invalid_or_expired_login: {
    title: "That sign-in link expired",
    detail: "Start a new sign-in to continue securely.",
  },
  access_not_provisioned: {
    title: "Your access is not ready",
    detail: "Ask your Diffex administrator to confirm your organization membership.",
  },
  authentication_failed: {
    title: "We could not complete sign in",
    detail: "Try again. If this continues, share the request ID with Diffex support.",
  },
  temporarily_unavailable: {
    title: "Sign in is temporarily unavailable",
    detail: "The authentication service needs a moment. Please try again shortly.",
  },
} as const;

type AuthenticationCode = keyof typeof authenticationMessages;

export function AuthenticationError() {
  const searchParams = new URLSearchParams(window.location.search);
  const rawCode = searchParams.get("code");
  const code = isAuthenticationCode(rawCode) ? rawCode : "authentication_failed";
  const message = authenticationMessages[code];
  const requestId = safeRequestId(searchParams.get("request_id"));

  return (
    <PublicLayout>
      <p className="eyebrow">Authentication</p>
      <h1>{message.title}</h1>
      <p>{message.detail}</p>
      {requestId ? <RequestId value={requestId} /> : null}
      <div className="public-actions">
        <ActionLink href="/login?return_to=/">Try again</ActionLink>
        <ActionLink href={productConfig.marketingOrigin} tone="secondary">
          Return to Diffex
        </ActionLink>
      </div>
    </PublicLayout>
  );
}

function isAuthenticationCode(value: string | null): value is AuthenticationCode {
  return value !== null && Object.hasOwn(authenticationMessages, value);
}

function safeRequestId(value: string | null): string | undefined {
  return value && /^[a-zA-Z0-9_-]{1,128}$/u.test(value) ? value : undefined;
}
